/**
 * COM-661 — which landing params the FSA <-> HSA tab carries, and how they are
 * put on the link. Pure / DOM-only so it unit-tests cleanly; the listener lives
 * in `TpaSiteSwitchTracker`. Sibling of `rev-share-params.ts`, whose readers it
 * reuses.
 */
import {
  TPA_BRANDING_COOKIE,
  getCookie,
  isExternal,
  resolveTpaBrandingParams,
} from "~/hec/utils/rev-share-params";

/** LinkTrust cookie, `TID~TEXP~TCID|PID~PEXP~PCID|BID~BEXP~BCID` (linktrust.server.ts). */
const TPA_COOKIE = "TPA";

/** Query params the destination store's landing path understands. */
export type SiteSwitchParams = Record<string, string>;

/**
 * The live parent ID from the `TPA` cookie's second slot, or null when the
 * slot is empty or its expiry has passed (an expired parent must not be
 * revived with a fresh expiry on the other store).
 */
export function readTpaParentId(
  cookieString: string = typeof document !== "undefined" ? document.cookie : "",
  now: number = Date.now(),
): string | null {
  const tpa = getCookie(TPA_COOKIE, cookieString);
  const [pid = "", pexp = ""] = (tpa?.split("|")[1] ?? "").split("~");
  if (!pid) return null;
  const expiry = Number(pexp);
  return Number.isFinite(expiry) && expiry > now ? pid : null;
}

/**
 * Decide which landing params the cross-store tab carries. The destination
 * re-runs its normal landing path on them, so both `tpaBranding` and `TPA` are
 * rebuilt there exactly as if the shopper had arrived from the original link —
 * no cookie-writing logic changes on either side.
 *
 * 1. Full affiliate (`AFID` + `GroupName` + `CID`) → those three, plus
 *    `ParentID` when the `TPA` cookie holds a live parent the AFID landing put
 *    there. A `GroupName=parent` landing IS the parent, so it is not repeated.
 * 2. Legacy `a_aid` (still in use — Sibiya, COM-661, 2026-10-05) → `a_aid`,
 *    plus `TP=1` when the `TPA` cookie holds a live parent (the Alegeus
 *    partner `TP=1` adds). An `a_aid` landing writes `tpaBranding` as
 *    `<a_aid>||` — the value survives as the first segment, so no reverse
 *    lookup through `linktrust-pap.json` is needed; the destination re-resolves
 *    it through the same mapping.
 *    An AFID-only landing (`?AFID=X`, also `X||`) takes this path too, and the
 *    destination writes the same `X||` cookie and, like the origin, no `TPA`.
 *
 * Current-URL params win over the cookies, matching SFRA's `urlParam || cookie`.
 */
export function resolveSiteSwitchParams(
  search: string = typeof window !== "undefined" ? window.location.search : "",
  cookieString: string = typeof document !== "undefined" ? document.cookie : "",
  now: number = Date.now(),
): SiteSwitchParams | null {
  const parentId = readTpaParentId(cookieString, now);

  const full = resolveTpaBrandingParams(search, cookieString);
  if (full) {
    const params: SiteSwitchParams = { ...full };
    const urlParentId = new URLSearchParams(search).get("ParentID");
    const carriedParent =
      urlParentId ||
      (full.GroupName.toLowerCase() !== "parent" ? parentId : null);
    if (carriedParent) params.ParentID = carriedParent;
    return params;
  }

  const urlParams = new URLSearchParams(search);
  const legacyId =
    urlParams.get("a_aid") ||
    getCookie(TPA_BRANDING_COOKIE, cookieString)?.split("|")[0] ||
    "";
  if (!legacyId) return null;
  const params: SiteSwitchParams = { a_aid: legacyId };
  if (urlParams.get("TP") === "1" || parentId) params.TP = "1";
  return params;
}

/**
 * Add `params` to the tab's href in place. No-op on a same-origin tab (the
 * dev-tier sibling remap can produce one), where the cookies already apply.
 * `searchParams.set` keeps the tab's own utm params untouched (COM-142) and
 * makes repeat clicks idempotent.
 */
export function applySiteSwitchParams(
  anchor: HTMLAnchorElement,
  params: SiteSwitchParams | null,
): boolean {
  if (!params || !anchor.getAttribute("href") || !isExternal(anchor.href)) {
    return false;
  }
  const url = new URL(anchor.href);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }
  anchor.href = url.href;
  return true;
}
